import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState } from '../types/auth';
import { User } from '../types/api';
import { apiService } from '../services/api';
import { STORAGE_KEYS } from '../constants';
import { queryClient } from '../utils/queryClient';

interface AuthStore extends Omit<AuthState, 'refreshToken'> {
  refreshToken: string | null;
  login: (tokenCode: string) => Promise<void>;
  logout: () => void;
  refreshTokenAsync: () => Promise<void>;
  setUser: (user: User) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (tokenCode: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await apiService.auth.login({ tokenCode });
          const { accessToken, refreshToken, user } = response.data.data;

          // Store tokens in localStorage
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));

          // Clear React Query cache to ensure fresh data after login
          queryClient.clear();

          // Set auth state
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Force invalidate all queries after a short delay to ensure auth state is set
          setTimeout(() => {
            queryClient.invalidateQueries();
          }, 100);

        } catch (error: any) {
          console.error('Auth store login error:', error);
          
          let errorMessage = 'Login failed. Please check your token code.';
          
          // Try multiple ways to extract the error message
          if (error.userMessage) {
            errorMessage = error.userMessage;
          } else if (error.serverMessage) {
            errorMessage = error.serverMessage;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          console.log('Setting auth error:', errorMessage);
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: () => {
        // Clear localStorage first to prevent any further API calls with invalid token
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER_DATA);

        // Clear React Query cache completely
        queryClient.clear();
        queryClient.removeQueries();

        // Update state immediately
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });

        // Skip logout API call to prevent 401 errors
        // The token is already cleared from localStorage, which is sufficient
        console.log('User logged out successfully');
      },

      refreshTokenAsync: async () => {
        try {
          const currentRefreshToken = get().refreshToken;
          if (!currentRefreshToken) {
            throw new Error('No refresh token available');
          }

          const response = await apiService.auth.refresh();
          const { accessToken } = response.data.data;

          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);

          set({
            accessToken,
            error: null,
          });
        } catch (error) {
          console.error('Token refresh failed:', error);
          get().logout();
          throw error;
        }
      },

      setUser: (user: User) => {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
        set({ user });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      initialize: async () => {
        try {
          set({ isLoading: true });

          // Check if we have stored auth data
          const storedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
          const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
          const storedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);

          if (storedToken && storedRefreshToken && storedUser) {
            // Check if token is expired (JWT tokens expire in 15 minutes = 900 seconds)
            try {
              const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
              const currentTime = Math.floor(Date.now() / 1000);
              const tokenExpiry = tokenPayload.exp;
              
              // If token is expired, clear auth data and force re-login
              if (tokenExpiry && currentTime > tokenExpiry) {
                console.log('Token expired, clearing auth data');
                localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
                localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
                localStorage.removeItem(STORAGE_KEYS.USER_DATA);
                
                set({
                  user: null,
                  accessToken: null,
                  refreshToken: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: null,
                });
                return;
              }
            } catch (tokenParseError) {
              console.log('Invalid token format, clearing auth data');
              localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
              localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
              localStorage.removeItem(STORAGE_KEYS.USER_DATA);
              
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              });
              return;
            }
            
            // Verify token is still valid by calling /me endpoint
            try {
              const response = await apiService.auth.me();
              const currentUser = response.data.data;

              set({
                user: currentUser,
                accessToken: storedToken,
                refreshToken: storedRefreshToken,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
            } catch (error) {
              console.log('Token validation failed, clearing auth data');
              // Token is invalid, clear auth data silently
              localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
              localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
              localStorage.removeItem(STORAGE_KEYS.USER_DATA);
              
              set({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              });
            }
          } else {
            set({ isLoading: false });
          }
        } catch (error) {
          console.error('Auth initialization failed:', error);
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'mindspire-planner-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);